import { useRef, useEffect, useState, useCallback } from 'react';
import { Box, Stack, IconButton, Tooltip, Divider, alpha } from '@mui/material';
import UndoIcon          from '@mui/icons-material/Undo';
import RectangleOutlined from '@mui/icons-material/RectangleOutlined';
import GestureIcon       from '@mui/icons-material/Gesture';
import ArrowForwardIcon  from '@mui/icons-material/ArrowForward';

export type AnnotationTool  = 'rect' | 'arrow' | 'freehand';
export type AnnotationColor = '#EF4444' | '#FBBF24' | '#34D399' | '#60A5FA';

interface Point { x: number; y: number }

interface Stroke {
  tool:   AnnotationTool;
  color:  AnnotationColor;
  points: Point[];
}

interface Props {
  imageSrc:  string;
  onExport:  (dataUrl: string) => void;
}

const COLORS: AnnotationColor[] = ['#EF4444', '#FBBF24', '#34D399', '#60A5FA'];
const STROKE_WIDTH = 3;

function drawArrow(ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number) {
  const headLen = 14;
  const angle   = Math.atan2(y2 - y1, x2 - x1);
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.lineTo(x2 - headLen * Math.cos(angle - Math.PI / 6), y2 - headLen * Math.sin(angle - Math.PI / 6));
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 - headLen * Math.cos(angle + Math.PI / 6), y2 - headLen * Math.sin(angle + Math.PI / 6));
  ctx.stroke();
}

function redraw(ctx: CanvasRenderingContext2D, img: HTMLImageElement, strokes: Stroke[], live?: Stroke) {
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  ctx.drawImage(img, 0, 0, ctx.canvas.width, ctx.canvas.height);
  [...strokes, ...(live ? [live] : [])].forEach((s) => {
    if (!s.points.length) return;
    ctx.strokeStyle = s.color;
    ctx.lineWidth   = STROKE_WIDTH;
    ctx.lineCap     = 'round';
    ctx.lineJoin    = 'round';

    if (s.tool === 'freehand') {
      ctx.beginPath();
      ctx.moveTo(s.points[0].x, s.points[0].y);
      s.points.slice(1).forEach((p) => ctx.lineTo(p.x, p.y));
      ctx.stroke();
    } else if (s.tool === 'rect' && s.points.length >= 2) {
      const [a, b] = [s.points[0], s.points[s.points.length - 1]];
      ctx.strokeRect(a.x, a.y, b.x - a.x, b.y - a.y);
    } else if (s.tool === 'arrow' && s.points.length >= 2) {
      const [a, b] = [s.points[0], s.points[s.points.length - 1]];
      drawArrow(ctx, a.x, a.y, b.x, b.y);
    }
  });
}

export function AnnotationCanvas({ imageSrc, onExport }: Props) {
  const canvasRef  = useRef<HTMLCanvasElement>(null);
  const imgRef     = useRef<HTMLImageElement | null>(null);
  const [tool,  setTool]  = useState<AnnotationTool>('rect');
  const [color, setColor] = useState<AnnotationColor>('#EF4444');
  const [strokes, setStrokes] = useState<Stroke[]>([]);
  const drawing   = useRef(false);
  const liveStroke = useRef<Stroke | null>(null);

  // Load image + init canvas
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width  = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d')!;
      redraw(ctx, img, []);
    };
    img.src = imageSrc;
  }, [imageSrc]);

  // Re-render on stroke changes
  useEffect(() => {
    const canvas = canvasRef.current;
    const img    = imgRef.current;
    if (!canvas || !img) return;
    redraw(canvas.getContext('2d')!, img, strokes);
  }, [strokes]);

  const getPos = (e: React.MouseEvent<HTMLCanvasElement>): Point => {
    const r = canvasRef.current!.getBoundingClientRect();
    const scaleX = canvasRef.current!.width  / r.width;
    const scaleY = canvasRef.current!.height / r.height;
    return { x: (e.clientX - r.left) * scaleX, y: (e.clientY - r.top) * scaleY };
  };

  const onMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    drawing.current  = true;
    liveStroke.current = { tool, color, points: [getPos(e)] };
  };

  const onMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!drawing.current || !liveStroke.current) return;
    const pos = getPos(e);
    if (tool === 'freehand') {
      liveStroke.current.points.push(pos);
    } else {
      liveStroke.current.points = [liveStroke.current.points[0], pos];
    }
    const canvas = canvasRef.current;
    const img    = imgRef.current;
    if (canvas && img) redraw(canvas.getContext('2d')!, img, strokes, liveStroke.current);
  };

  const onMouseUp = () => {
    if (!drawing.current || !liveStroke.current) return;
    drawing.current = false;
    if (liveStroke.current.points.length > 1) {
      setStrokes((prev) => [...prev, liveStroke.current!]);
    }
    liveStroke.current = null;
  };

  const undo = useCallback(() => setStrokes((prev) => prev.slice(0, -1)), []);

  const handleExport = useCallback(() => {
    onExport(canvasRef.current!.toDataURL('image/png'));
  }, [onExport]);

  // Auto-export whenever strokes change (parent gets live preview)
  useEffect(() => {
    if (!canvasRef.current || !imgRef.current) return;
    onExport(canvasRef.current.toDataURL('image/png'));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strokes]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%' }}>
      {/* Toolbar */}
      <Stack direction="row" alignItems="center" spacing={0.5}
        sx={{ px: 1, py: 0.5, bgcolor: '#1A1A1A', borderRadius: 1.5, flexShrink: 0 }}>

        <Tooltip title="Rectángulo"><IconButton size="small"
          onClick={() => setTool('rect')}
          sx={{ color: tool === 'rect' ? '#fff' : 'text.secondary', bgcolor: tool === 'rect' ? alpha('#fff', 0.1) : 'transparent' }}>
          <RectangleOutlined fontSize="small" />
        </IconButton></Tooltip>

        <Tooltip title="Flecha"><IconButton size="small"
          onClick={() => setTool('arrow')}
          sx={{ color: tool === 'arrow' ? '#fff' : 'text.secondary', bgcolor: tool === 'arrow' ? alpha('#fff', 0.1) : 'transparent' }}>
          <ArrowForwardIcon fontSize="small" />
        </IconButton></Tooltip>

        <Tooltip title="Dibujo libre"><IconButton size="small"
          onClick={() => setTool('freehand')}
          sx={{ color: tool === 'freehand' ? '#fff' : 'text.secondary', bgcolor: tool === 'freehand' ? alpha('#fff', 0.1) : 'transparent' }}>
          <GestureIcon fontSize="small" />
        </IconButton></Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {COLORS.map((c) => (
          <Box key={c} onClick={() => setColor(c)} sx={{
            width: 18, height: 18, borderRadius: '50%', bgcolor: c, cursor: 'pointer', flexShrink: 0,
            border: color === c ? '2px solid #fff' : '2px solid transparent',
            transition: 'border 0.15s',
          }} />
        ))}

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <Tooltip title="Deshacer"><span>
          <IconButton size="small" onClick={undo} disabled={!strokes.length}
            sx={{ color: 'text.secondary' }}>
            <UndoIcon fontSize="small" />
          </IconButton>
        </span></Tooltip>

        <Box sx={{ flex: 1 }} />
        <Box
          onClick={handleExport}
          sx={{
            fontSize: 11, fontWeight: 700, color: 'var(--brand-on)',
            bgcolor: 'var(--brand-accent)', borderRadius: 1, px: 1.5, py: 0.5,
            cursor: 'pointer', '&:hover': { bgcolor: 'var(--brand-accent-strong)' },
          }}
        >
          Usar captura
        </Box>
      </Stack>

      {/* Canvas */}
      <Box sx={{ flex: 1, overflow: 'auto', borderRadius: 1, border: '1px solid rgba(255,255,255,0.08)' }}>
        <canvas
          ref={canvasRef}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          style={{ display: 'block', maxWidth: '100%', cursor: 'crosshair' }}
        />
      </Box>
    </Box>
  );
}
